import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Award, BookOpen, CalendarDays, Library, ScrollText, Users } from "lucide-react";

import { curriculumService } from "../services/curriculumService";
import { examinationService } from "../services/examinationService";
import { usePermissions } from "../hooks/usePermissions";
import { useAuthStore } from "../stores/authStore";
import CalendarPanel from "../components/academics/CalendarPanel";
import { CurriculaPanel, SubjectsPanel } from "../components/academics/CurriculumPanels";
import { ExamsPanel, SyllabiPanel } from "../components/academics/TeachingRecordPanels";
import { withCurriculumCounts, withSyllabusCounts } from "../components/academics/academicsLogic";

type Tab = "calendar" | "subjects" | "curricula" | "syllabi" | "exams";
const TABS: { id: Tab; label: string; icon: typeof BookOpen }[] = [
  { id: "calendar", label: "School calendar", icon: CalendarDays },
  { id: "subjects", label: "Subjects", icon: BookOpen },
  { id: "curricula", label: "Curricula", icon: Library },
  { id: "syllabi", label: "Syllabi", icon: ScrollText },
  { id: "exams", label: "Examinations", icon: Award },
];

// The same role sets the server checks: years are school leadership's, terms
// are also the director of studies' (apps/core/views.py, uganda_views.py).
const YEAR_WRITERS = ["admin", "director", "principal", "head_teacher"];
const TERM_WRITERS = [...YEAR_WRITERS, "director_of_studies"];
const CLASS_SETUP_ROLES = ["admin", "director", "head_teacher", "director_of_studies"];

export default function Academics() {
  const [params, setParams] = useSearchParams();
  const tab: Tab = TABS.some((item) => item.id === params.get("tab")) ? (params.get("tab") as Tab) : "calendar";
  const [openSubject, setOpenSubject] = useState<number | null>(null);
  const role = useAuthStore((state) => state.user?.user_type) ?? "";
  const { hasCapability } = usePermissions();
  const canWriteCurriculum = hasCapability("access.curriculum.leadership");

  const curricula = useQuery({ queryKey: ["academics-curricula"], queryFn: curriculumService.getAllCurricula });
  const subjects = useQuery({ queryKey: ["academics-subjects"], queryFn: curriculumService.getAllSubjects });
  const syllabi = useQuery({ queryKey: ["syllabi"], queryFn: curriculumService.getSyllabi });
  const exams = useQuery({ queryKey: ["exams"], queryFn: examinationService.getExams });

  const selectTab = (next: Tab) => {
    setOpenSubject(null);
    setParams(next === "calendar" ? {} : { tab: next }, { replace: true });
  };
  const counts: Partial<Record<Tab, number | undefined>> = {
    subjects: subjects.data?.length,
    curricula: curricula.data?.length,
    syllabi: syllabi.data?.length,
    exams: exams.data?.length,
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Academics</h1>
          <p className="mt-1 text-sm text-gray-500">The school calendar, what is taught, and how it is examined.</p>
        </div>
        {CLASS_SETUP_ROLES.includes(role) && (
          <Link to="/class-setup" className="btn btn-outline gap-2">
            <Users className="h-4 w-4" /> Classes & streams
          </Link>
        )}
      </div>

      <nav role="tablist" aria-label="Academics sections" className="-mb-px flex gap-x-6 overflow-x-auto border-b border-gray-200">
        {TABS.map((item) => {
          const active = tab === item.id;
          return (
            <button
              key={item.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => selectTab(item.id)}
              className={`inline-flex shrink-0 items-center gap-2 border-b-2 px-1 py-2.5 text-sm font-medium ${
                active ? "border-primary-600 text-primary-700" : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
            >
              <item.icon className="h-4 w-4" />
              {item.label}
              {counts[item.id] !== undefined && (
                <span className={`rounded-full px-1.5 py-0.5 text-xs tabular-nums ${active ? "bg-primary-100 text-primary-700" : "bg-gray-100 text-gray-600"}`}>
                  {counts[item.id]}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {tab === "calendar" && (
        <CalendarPanel
          canWriteYears={YEAR_WRITERS.includes(role)}
          canWriteTerms={TERM_WRITERS.includes(role)}
          canDeleteTerms={YEAR_WRITERS.includes(role)}
        />
      )}
      {tab === "subjects" && (
        <SubjectsPanel
          openId={openSubject}
          rows={withSyllabusCounts(subjects.data, syllabi.data)}
          curricula={curricula.data ?? []}
          syllabi={syllabi.data ?? []}
          isLoading={subjects.isLoading}
          isError={subjects.isError}
          onRetry={() => subjects.refetch()}
          canWrite={canWriteCurriculum}
        />
      )}
      {tab === "curricula" && (
        <CurriculaPanel
          rows={withCurriculumCounts(curricula.data, subjects.data, syllabi.data)}
          subjects={subjects.data ?? []}
          isLoading={curricula.isLoading}
          isError={curricula.isError}
          onRetry={() => curricula.refetch()}
          canWrite={canWriteCurriculum}
          onOpenSubject={(subject) => {
            setParams({ tab: "subjects" }, { replace: true });
            setOpenSubject(subject.id);
          }}
        />
      )}
      {tab === "syllabi" && (
        <SyllabiPanel rows={syllabi.data} isLoading={syllabi.isLoading} isError={syllabi.isError} onRetry={() => syllabi.refetch()} />
      )}
      {tab === "exams" && (
        <ExamsPanel rows={exams.data} isLoading={exams.isLoading} isError={exams.isError} onRetry={() => exams.refetch()} />
      )}
    </div>
  );
}
